import { Injectable } from '@angular/core';
import { ConfigRepository } from './config.repository';
import { AdditionalPageShortDto } from '../../api/v1/models/additional-page-short-dto';
import { catchError, combineLatestWith, Observable, of, switchMap, tap } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { AdditionalPagesService } from '../../api/v1/services/additional-pages.service';
import { PromotionsService } from '../../api/v1/services/promotions.service';
import { PromotionShort } from '../../api/v1/models/promotion-short';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import { selectIsRequestPending, updateRequestStatus, withRequestsStatus } from '@ngneat/elf-requests';
import { PromotionDto } from '../../api/v1/models/promotion-dto';
import { Router } from '@angular/router';
import { AdditionalPageDto } from '../../api/v1/models/additional-page-dto';

interface AdditionalPagesModel {
  additionalPagesShortMap: {[p: string]: AdditionalPageShortDto} | null,
  additionalSlugToIdMap: {[p: string]: string} | null,
  promotionSlugToIdMap: {[p: string]: string} | null,
  promotionsShort: PromotionShort[] | null,
}

const additionalPagesStore = createStore(
  { name: 'additionalPages' },
  withProps<AdditionalPagesModel>({
    additionalPagesShortMap: null,
    additionalSlugToIdMap: null,
    promotionSlugToIdMap: null,
    promotionsShort: null,
  }),
  withRequestsStatus<'additionalPageLoading'>(),
  withRequestsStatus<'promotionListLoading'>(),
  withRequestsStatus<'promotionLoading'>(),
);

@Injectable({
  providedIn: 'root'
})
export class AdditionalPagesRepository {
  constructor(
    private router: Router,
    private config: ConfigRepository,
    private additionalPagesApi: AdditionalPagesService,
    private promotionsApi: PromotionsService,
  ) {
    this.getAdditionalMaps().subscribe();
  }

  additionalPageLoading$: Observable<boolean> = additionalPagesStore.pipe(selectIsRequestPending('additionalPageLoading'));
  promotionListLoading$: Observable<boolean> = additionalPagesStore.pipe(selectIsRequestPending('promotionListLoading'));
  promotionLoading$: Observable<boolean> = additionalPagesStore.pipe(selectIsRequestPending('promotionLoading'));

  private additionalPagesShortMap$: Observable<{[p: string]: AdditionalPageShortDto}> = additionalPagesStore.pipe(
    select(state => state.additionalPagesShortMap),
    filter((additionalPagesShortMap): additionalPagesShortMap is {[p: string]: AdditionalPageShortDto} => !!additionalPagesShortMap)
  );

  private additionalSlugToIdMap$: Observable<{[p: string]: string}> = additionalPagesStore.pipe(
    select(state => state.additionalSlugToIdMap),
    filter((additionalSlugToIdMap): additionalSlugToIdMap is {[p: string]: string} => !!additionalSlugToIdMap)
  );

  private getAdditionalMaps(): Observable<unknown>{
    return this.config.additionalPagesNew$.pipe(
      filter(pagesShort => !!pagesShort),
      tap(pagesShort => {
        const idToItemMap: {[key: string]: AdditionalPageShortDto} = {};
        const slugToIdMap: {[key: string]: string} = {};

        Object.keys(pagesShort!).forEach(key => {
          pagesShort![key].forEach(page => {
            idToItemMap[page.id] = page;
            slugToIdMap[page.slug] = page.id;
          })
        })

        additionalPagesStore.update(setProp('additionalPagesShortMap', idToItemMap))
        additionalPagesStore.update(setProp('additionalSlugToIdMap', slugToIdMap))
      }),
    )
  }


  getShortPageById(id: string): Observable<AdditionalPageShortDto | null> {
    return this.additionalPagesShortMap$.pipe(map(map => map[id] || null))
  }

  getPageShortBySlug(slug: string): Observable<AdditionalPageShortDto | null> {
    return this.additionalSlugToIdMap$.pipe(
      switchMap(map => this.getShortPageById(map[slug]))
    )
  }

  getAdditionalPageById(id: string, name: string): Observable<AdditionalPageDto> {
    additionalPagesStore.update(updateRequestStatus('additionalPageLoading', 'pending'));

    return this.additionalPagesApi.apiV1AdditionalPagesIdGet$Json({ id }).pipe(
      tap(() => additionalPagesStore.update(updateRequestStatus('additionalPageLoading', 'success'))),
      map(response => {
        if (response.error || !response.result) {
          throw response;
        }
        return response.result;
      }),

      catchError(error => {
        additionalPagesStore.update(updateRequestStatus('additionalPageLoading', 'error', error));
        throw new Error('Additional page "' + name + '" is not found');
      })
    )
  }

  promotions$: Observable<PromotionShort[] | null> = additionalPagesStore.pipe(
    select(state => state.promotionsShort),
    switchMap(promotionsShort => promotionsShort ? of(promotionsShort) : this.fetchPromotions())
  )

  fetchPromotions(): Observable<PromotionShort[]>{
    additionalPagesStore.update(updateRequestStatus('promotionListLoading', 'pending'));
    return this.config.promotionCount$.pipe(
      combineLatestWith(this.config.currentLocale$),
      switchMap(([count, currentLocale]) => this.promotionsApi.apiV1PromotionsGet$Json({Limit: count, Language: currentLocale})),
      map(response => {
        if(response.error || !response.result) {
          throw response;
        }
        return response.result;
      }),
      tap(promotionList => {
        const slugToIdMap: {[key: string]: string} = {};

        promotionList.forEach(promotion => {
            slugToIdMap[promotion.slug] = promotion.id;
        })

        additionalPagesStore.update(setProp('promotionSlugToIdMap', slugToIdMap));
        additionalPagesStore.update(setProp('promotionsShort', promotionList));
      }),
      tap(() => additionalPagesStore.update(updateRequestStatus('promotionListLoading', 'success'))),
      catchError(error => {
        additionalPagesStore.update(updateRequestStatus('promotionListLoading', 'error', error));
        throw new Error('Action list is not found');
      })
    )
  }

  fetchPromotionBySlug(slug: string): Observable<PromotionDto> {
    return additionalPagesStore.pipe(
      select(state => state.promotionSlugToIdMap),
      filter((promotionSlugToIdMap): promotionSlugToIdMap is {[p: string]: string} => !!promotionSlugToIdMap),
      tap(promotionSlugToIdMap => {
        if(promotionSlugToIdMap[slug]){return}

        void this.router.navigate(['page-not-found']);
      }),
      filter(promotionSlugToIdMap => !!promotionSlugToIdMap[slug]),
      switchMap(promotionSlugToIdMap => this.fetchPromotionById(promotionSlugToIdMap[slug])),
    )
  }

  fetchPromotionById(id: string): Observable<PromotionDto>{
    additionalPagesStore.update(updateRequestStatus('promotionLoading', 'pending'));
    return this.promotionsApi.apiV1PromotionsIdGet$Json({id: id}).pipe(
      map(response => {
        if(response.error || !response.result) {
          throw response;
        }
        return response.result;
      }),
      tap(() => additionalPagesStore.update(updateRequestStatus('promotionLoading', 'success'))),
      catchError(error => {
        additionalPagesStore.update(updateRequestStatus('promotionLoading', 'error', error));
        throw new Error('Action list is not found');
      })
    )
  }
}
